import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2, X } from "lucide-react";

/**
 * Domain picker fed by the sop_domains table (the approved category set).
 * The database enforces the same set with a foreign key, so a made-up value
 * can never reach a member as a raw section header. Adding a category is a
 * deliberate act: it inserts to sop_domains first, then selects it.
 */
export function DomainSelect({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (v: string) => void;
}) {
  const { toast } = useToast();
  const [domains, setDomains] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    const { data } = await supabase
      .from("sop_domains" as any)
      .select("domain,sort_order")
      .order("sort_order", { ascending: true })
      .order("domain", { ascending: true });
    setDomains(((data as any[]) || []).map((r) => r.domain as string));
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  async function addDomain() {
    const d = newDomain.trim();
    if (!d) return;
    setSaving(true);
    const { error } = await supabase
      .from("sop_domains" as any)
      .insert({ domain: d, sort_order: 400 } as any);
    setSaving(false);
    if (error) {
      toast({ title: "Could not add category", description: error.message, variant: "destructive" });
      return;
    }
    setNewDomain("");
    setAdding(false);
    await load();
    onChange(d);
    toast({ title: "Category added", description: d });
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Select value={value || ""} onValueChange={onChange} disabled={loading}>
          <SelectTrigger className="flex-1">
            <SelectValue placeholder={loading ? "Loading categories" : "Choose a category"} />
          </SelectTrigger>
          <SelectContent>
            {domains.map((d) => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setAdding((v) => !v)}
          aria-label={adding ? "Cancel new category" : "Add a category"}
        >
          {adding ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </Button>
      </div>

      {adding && (
        <div className="flex items-center gap-2">
          <Input
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder="New category name, exactly as members will read it"
          />
          <Button type="button" size="sm" onClick={addDomain} disabled={saving || !newDomain.trim()}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
          </Button>
        </div>
      )}

      {value && !loading && !domains.includes(value) && (
        <p className="text-xs text-destructive">
          This document's current category is not in the approved set. Pick one from the list before saving.
        </p>
      )}
    </div>
  );
}
